<template>
  <div
    v-if="routeChunkError"
    class="fixed inset-0 z-[9999] flex h-screen w-full items-center justify-center bg-surf-bg-def"
  >
    <progress-error :error-message="routeChunkError" />
  </div>
  <component
    :is="layout"
    v-else-if="layout"
  >
    <router-view
      :active-aside="activeAside"
    />
  </component>
  <wui-dialog-wrapper />
  <wui-notify-wrapper />
</template>

<script lang="ts" setup>
import type { EventCallback } from '@tauri-apps/api/event'
import { useDialog, WuiDialogWrapper, authPluginKey, safeInject, WuiNotifyWrapper, AuthApi } from '@wui/common-library'
import { DefineComponent, shallowRef, watchEffect, ref, watch, computed, defineAsyncComponent } from 'vue'
import { useRoute } from 'vue-router'

import { ImageDialog } from '@/widgets/image-dialog'
import { DialogWrapper } from '@/widgets/rtc-session-dialog'

import type { ActiveAsideType } from '@/features/call-page-aside'
import { useTimezoneSelector } from '@/features/timezone-selector'

import { useLiveCallSessions } from '@/entities/calls'
import { useStoreSettingsNotifications } from '@/entities/notification'
import { usePreferencesStore } from '@/entities/preference'
import { useNotification } from '@/entities/use-notification'
import { registerCallSessionDialog, useWebRTC } from '@/entities/webrtc'

import { ProgressError } from '@/shared/components/loaders'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { useTauriEventListener } from '@/shared/composables/tauri'
import { useForceReloadSignal } from '@/shared/composables/use-force-reload-signal'
import { registerImageDialog } from '@/shared/lib/image-dialog-bridge'
import { CustomTauriEvent } from '@/shared/types/tauri'
import { isPWAWindow } from '@/shared/utils'
import { tauriMode } from '@/shared/utils/import-meta'
import { handlerSystemTray } from '@/shared/utils/tauri'

import { routeChunkError } from './router'

const ReconnectDialog = defineAsyncComponent(() => import('@/widgets/reconnect-dialog').then((m) => m.ReconnectDialog))

const { t } = useLocalization()
const settingsNotificationsStore = useStoreSettingsNotifications()

usePreferencesStore()
useForceReloadSignal()

const { showDialog, closeDialog } = useDialog()
const { resetCallStatusIfChangedByCall } = settingsNotificationsStore
const { currentUser, initCurrentUser } = useCurrentUser()
initCurrentUser()

registerCallSessionDialog((sessionId, bindCloseDialog) => {
  const promise = showDialog(DialogWrapper, {
    sessionId,
    hasOverlay: true,
  })
  // createWrappedDialog синхронно пишет close в ref того же useDialog()
  if (closeDialog.value) {
    bindCloseDialog(closeDialog.value)
  }
  return promise
})

registerImageDialog((props) => showDialog(ImageDialog, props))


const { token } = safeInject<AuthApi>(authPluginKey)

const layout = shallowRef<DefineComponent>()
const route = useRoute()
const activeAside = ref<ActiveAsideType>(null) // Для GuestCallSession

if (tauriMode()) {
  const tauriEvent = useTauriEventListener()
  tauriEvent.listenEvent(CustomTauriEvent.SYSTEM_TRAY_EVENT, handlerSystemTray as EventCallback<unknown>)
}

if (token.value && currentUser.value) {
  // В роутере на данный момент path= "/"
  if (window.location.pathname.includes('/guest-')) {
    localStorage.clear()
  }
}

useTimezoneSelector().refreshTzSettings()

watchEffect(() => {
  if (route.meta?.layout) {
    layout.value = route.meta.layout as DefineComponent
  }
})

const onBeforeUnload = (event: Event): void => {
  beforeCloseApp()
  if (isPWAWindow()) {
    event.preventDefault()
    event.returnValue = false
    showDialog(ReconnectDialog)
  } else {
    useNotification().closeAllNotifications()
  }
}

const beforeCloseApp = () => {
  useLiveCallSessions().liveCallSessions.value.forEach(session => session.terminate())
  useWebRTC().stop()

  resetCallStatusIfChangedByCall()
}

window.addEventListener('beforeunload', onBeforeUnload, { capture: true })

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    useNotification().closeAllNotifications()
  }
})

const metaTitle = computed(() => {
  const hasTitle = !!(route.meta.title as string)?.length
  const text: string = hasTitle ? t(route.meta.title as string) : '📱'
  if (isPWAWindow()) {
    return text
  }
  const clientName = tauriMode() ? t('about.rtuDesktopClient') : t('about.rtuClient')
  return `${text} - ${clientName} (${t('terminals.softPhone')})`
})

watch(() => metaTitle.value, (title) => {
  document.title = title
})
</script>
